import { redirect } from 'react-router';
import { isStoreService } from '../../helpers/appHelper';
import { getService } from '../../helpers/serviceHelper';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
} from '../../helpers/urlHelper';
import { updateSelectedService } from '../../reducers/app/appReducer';
import type { AvailableService } from '../../services/appointment/model';
import store from '../../store';
import { loadServices } from './servicesPageLoader';
import { getStores } from '../../reducers/stores/storesReducer';
import type { AppView } from '../../types/app';
import { resetSessionState } from '../middleware/middlewareHelper';

const getSelectedService = async (view: AppView): Promise<AvailableService> => {
  let availableServices = store.getState().services.availableServices;
  const operation = store.getState().app.operation;
  const selectedServiceId = store.getState().app.selected.serviceId;

  if (availableServices.length === 0) {
    availableServices = await loadServices(operation);
  }

  const selectedService = getService(selectedServiceId, availableServices);
  if (!selectedService) {
    const startPagePath = getPagePath(operation, view);
    resetSessionState();
    throw redirect(addPersistingQueryParamsToPath(startPagePath));
  }
  store.dispatch(
    updateSelectedService({
      serviceId: selectedService.serviceId,
      serviceCode: selectedService.serviceCode,
    }),
  );
  return selectedService;
};

/**
 * Loads services and stores if the data is not yet available. This is valid
 * when navigating directly to timeslots page or the page is reloaded on
 * timeslots page, contact page or confirm page.
 */
export const loadDataForSelectedService = async (
  view: AppView,
): Promise<{
  service: AvailableService;
  storeId: string;
}> => {
  const service = await getSelectedService(view);
  const availableStores = store.getState().stores.availableStores;
  let storeId = store.getState().app.selected.storeId;
  if (service && isStoreService(service.location) && !availableStores.length) {
    try {
      const { selectedStoreId } = await store
        .dispatch(
          getStores({
            serviceProductId: service.serviceId,
            serviceLocation: service.location,
          }),
        )
        .unwrap();
      storeId = selectedStoreId; // verify storeId value
    } catch {
      const operation = store.getState().app.operation;
      const startPagePath = getPagePath(operation, view);
      resetSessionState();
      throw redirect(addPersistingQueryParamsToPath(startPagePath));
    }
  }
  return { service, storeId };
};
